import { forwardRef, useState, ReactNode, useRef } from 'react';

export interface SwipeableProps {
  children: ReactNode;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  className?: string;
}

// https://stackoverflow.com/questions/70612769/how-do-i-recognize-swipe-events-in-react
export const Swipeable = forwardRef<HTMLDivElement, SwipeableProps>(
  (
    { children, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, ...props },
    ref,
  ) => {
    const fallbackRef = useRef<HTMLDivElement>(null);
    const elRef =
      (ref as React.MutableRefObject<HTMLDivElement | null>) || fallbackRef;

    const [touchStartX, setTouchStartX] = useState<number | null>(null);
    const [touchEndX, setTouchEndX] = useState<number | null>(null);

    const [touchStartY, setTouchStartY] = useState<number | null>(null);
    const [touchEndY, setTouchEndY] = useState<number | null>(null);

    const minSwipeDistance = 50;

    function onTouchStart(e: React.TouchEvent<HTMLDivElement>) {
      const t = e.targetTouches[0];
      if (!t) return;
      setTouchEndX(null);
      setTouchStartX(t.clientX);
      setTouchEndY(null);
      setTouchStartY(t.clientY);
    }

    function onTouchMove(e: React.TouchEvent<HTMLDivElement>) {
      const t = e.targetTouches[0];
      if (!t) return;

      setTouchEndX(t.clientX);
      setTouchEndY(t.clientY);

      if (elRef?.current && touchStartX != null && touchStartY != null) {
        elRef.current.style.setProperty(
          '--swipe-offset-x',
          `${t.clientX - touchStartX}px`,
        );
        elRef.current.style.setProperty(
          '--swipe-offset-y',
          `${t.clientY - touchStartY}px`,
        );
      }
    }

    function onTouchEnd() {
      if (touchStartX != null && touchEndX != null) swipeHorizontal();
      if (touchStartY != null && touchEndY != null) swipeVertical();

      if (elRef?.current) {
        elRef.current.style.setProperty('--swipe-offset-x', '0');
        elRef.current.style.setProperty('--swipe-offset-y', '0');
      }
    }

    function swipeHorizontal() {
      if (
        touchStartX == null ||
        touchEndX == null ||
        touchStartY == null ||
        touchEndY == null
      )
        return;
      const xDistance = touchStartX - touchEndX;
      const yDistance = touchStartY - touchEndY;
      if (Math.abs(yDistance) >= Math.abs(xDistance)) return;

      if (xDistance > minSwipeDistance) onSwipeLeft?.();
      if (xDistance < -minSwipeDistance) onSwipeRight?.();
    }

    function swipeVertical() {
      if (
        touchStartX == null ||
        touchEndX == null ||
        touchStartY == null ||
        touchEndY == null
      )
        return;
      const xDistance = touchStartX - touchEndX;
      const yDistance = touchStartY - touchEndY;
      if (Math.abs(xDistance) >= Math.abs(yDistance)) return;

      if (yDistance > minSwipeDistance) onSwipeUp?.();
      if (yDistance < -minSwipeDistance) onSwipeDown?.();
    }

    return (
      <div
        ref={elRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        {...props}
      >
        {children}
      </div>
    );
  },
);
